import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ClientSearch from '../components/ClientSearch';

/* ── helpers ── */
function formatTime(date) {
  return new Date(date).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}
function formatDate(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Zojuist';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} min geleden`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} uur geleden`;
  if (diff < 172800000) return 'Gisteren';
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

/* Light markdown renderer */
function renderMarkdown(text) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  const inlineFormat = (str) => {
    let s = str
      .replace(/`([^`]+)`/g, '‹CODE›$1‹/CODE›')
      .replace(/\*\*(.+?)\*\*/g, '‹B›$1‹/B›')
      .replace(/\*(.+?)\*/g, '‹I›$1‹/I›');
    return s.split(/(‹CODE›.*?‹\/CODE›|‹B›.*?‹\/B›|‹I›.*?‹\/I›)/).map((tok, j) => {
      if (tok.startsWith('‹CODE›')) return <code key={j} className="bg-black/20 px-1 rounded text-sm font-mono">{tok.slice(6, -7)}</code>;
      if (tok.startsWith('‹B›')) return <strong key={j}>{tok.slice(3, -4)}</strong>;
      if (tok.startsWith('‹I›')) return <em key={j}>{tok.slice(3, -3)}</em>;
      return tok;
    });
  };
  while (i < lines.length) {
    const line = lines[i];
    const hm = line.match(/^(#{1,3})\s+(.*)/);
    if (hm) {
      const lvl = hm[1].length;
      const cls = lvl === 1 ? 'text-xl font-bold mt-3 mb-1' : lvl === 2 ? 'text-lg font-semibold mt-2 mb-1' : 'font-semibold mt-2';
      elements.push(<div key={i} className={cls}>{inlineFormat(hm[2])}</div>); i++; continue;
    }
    if (line.match(/^[-*•]\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[-*•]\s+/)) {
        items.push(<li key={i} className="ml-4 list-disc">{inlineFormat(lines[i].replace(/^[-*•]\s+/, ''))}</li>); i++;
      }
      elements.push(<ul key={`ul-${i}`} className="space-y-0.5 my-1">{items}</ul>); continue;
    }
    if (line.match(/^\d+\.\s+/)) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s+/)) {
        items.push(<li key={i} className="ml-4 list-decimal">{inlineFormat(lines[i].replace(/^\d+\.\s+/, ''))}</li>); i++;
      }
      elements.push(<ol key={`ol-${i}`} className="space-y-0.5 my-1">{items}</ol>); continue;
    }
    if (line.match(/^---+$/)) { elements.push(<hr key={i} className="border-white/20 my-2" />); i++; continue; }
    if (line.trim() === '') { elements.push(<div key={i} className="h-2" />); i++; continue; }
    elements.push(<div key={i}>{inlineFormat(line)}</div>); i++;
  }
  return elements;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3">
      {[0, 1, 2].map(d => (
        <span key={d} className="w-2 h-2 rounded-full"
          style={{ backgroundColor: 'var(--brand)', opacity: 0.6, animation: `chatBounce 1s ease-in-out ${d * 0.15}s infinite` }} />
      ))}
    </div>
  );
}

function AnimatedBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0" style={{ background: 'var(--chat-bg)' }} />
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-30"
        style={{ background: 'radial-gradient(circle, var(--chat-orb-1), transparent 70%)', animation: 'chatFloatA 18s ease-in-out infinite' }} />
      <div className="absolute top-1/3 -right-48 w-[500px] h-[500px] rounded-full opacity-25"
        style={{ background: 'radial-gradient(circle, var(--chat-orb-2), transparent 70%)', animation: 'chatFloatB 22s ease-in-out infinite' }} />
      <div className="absolute -bottom-32 left-1/3 w-[450px] h-[450px] rounded-full opacity-20"
        style={{ background: 'radial-gradient(circle, var(--chat-orb-3), transparent 70%)', animation: 'chatFloatC 26s ease-in-out infinite' }} />
      <div className="absolute top-1/4 left-1/2 w-[300px] h-[300px] rounded-full opacity-15"
        style={{ background: 'radial-gradient(circle, var(--chat-orb-4), transparent 70%)', animation: 'chatFloatA 30s ease-in-out 4s infinite reverse' }} />
      <div className="absolute inset-0" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
      }} />
    </div>
  );
}

const SUGGESTIONS = [
  'Wat zijn de top 5 reisbestemmingen voor 2025?',
  'Hoe schrijf ik een overtuigende reisbrochure?',
  'Welke inentingen zijn nodig voor Thailand?',
  'Geef me ideeën voor een huwelijksreis in Europa',
  'Wat zijn de voordelen van een all-inclusive vakantie?',
  'Vertaal "Welkom bij ons reisvoorstel" naar Engels',
];

export default function Chat() {
  const { authFetch, user } = useAuth();

  // chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentConvId, setCurrentConvId] = useState(null);

  // booking context state
  const [bookings, setBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [bookingSearch, setBookingSearch] = useState('');

  // client context state
  const [selectedClient, setSelectedClient] = useState(null);

  // sidebar state
  const [conversations, setConversations] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  // ── load conversation list ──
  const loadConversations = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const res = await authFetch('/api/chat/conversations');
      const data = await res.json();
      if (res.ok) setConversations(data);
    } catch (_) {}
    setLoadingConvs(false);
  }, [authFetch]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // load bookings for context selector
  useEffect(() => {
    authFetch('/api/bookings')
      .then(r => r.ok ? r.json() : [])
      .then(data => setBookings(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [authFetch]);

  // ── auto-scroll ──
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  // ── auto-resize textarea ──
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
  }, [input]);

  // ── save / update conversation in DB ──
  const persistConversation = useCallback(async (msgs, convId) => {
    if (!msgs.length) return convId;
    const title = msgs[0]?.content?.slice(0, 70) || 'Gesprek';
    try {
      if (convId) {
        await authFetch(`/api/chat/conversations/${convId}`, {
          method: 'PUT',
          body: JSON.stringify({ messages: msgs.map(({ role, content }) => ({ role, content })), title }),
        });
        return convId;
      } else {
        const res = await authFetch('/api/chat/conversations', {
          method: 'POST',
          body: JSON.stringify({ messages: msgs.map(({ role, content }) => ({ role, content })), title }),
        });
        const data = await res.json();
        return data.id || null;
      }
    } catch (_) { return convId; }
  }, [authFetch]);

  // ── send message ──
  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setError('');

    const newMessages = [...messages, { role: 'user', content: msg, ts: new Date() }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const payload = newMessages.map(({ role, content }) => ({ role, content }));
      const res = await authFetch('/api/chat/message', {
        method: 'POST',
        body: JSON.stringify({
          messages:   payload,
          booking_id: selectedBookingId || null,
          client_id:  selectedClient?.client_id ?? null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fout bij ophalen antwoord');

      const allMessages = [...newMessages, { role: 'assistant', content: data.reply, ts: new Date() }];
      setMessages(allMessages);

      // auto-save
      const newId = await persistConversation(allMessages, currentConvId);
      if (newId && !currentConvId) setCurrentConvId(newId);
      loadConversations();
    } catch (e) {
      setError(e.message);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }, [input, messages, loading, authFetch, currentConvId, persistConversation, loadConversations]);

  // ── open saved conversation ──
  const openConversation = (conv) => {
    setCurrentConvId(conv.id);
    setMessages(conv.messages.map(m => ({ ...m, ts: new Date(conv.updated_at) })));
    setError('');
  };

  // ── start new chat ──
  const newChat = () => {
    setMessages([]);
    setCurrentConvId(null);
    setError('');
    setInput('');
  };

  // ── delete conversation ──
  const deleteConversation = async (e, id) => {
    e.stopPropagation();
    setDeletingId(id);
    await authFetch(`/api/chat/conversations/${id}`, { method: 'DELETE' });
    if (currentConvId === id) newChat();
    await loadConversations();
    setDeletingId(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="relative flex h-screen bg-gray-50">

      {/* ── conversation history sidebar ── */}
      <div
        className="relative z-20 flex flex-col flex-shrink-0 transition-all duration-300 sidebar-gradient"
        style={{
          width: sidebarOpen ? 260 : 0,
          overflow: 'hidden',
          borderRight: sidebarOpen ? '1px solid rgba(255,255,255,0.1)' : 'none',
        }}
      >
        <div className="flex flex-col h-full" style={{ width: 260 }}>
          {/* sidebar header */}
          <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <p className="text-white font-semibold text-sm">Gesprekken</p>
              <p className="text-white/40 text-xs">Laatste 5 opgeslagen</p>
            </div>
            <button
              onClick={newChat}
              title="Nieuw gesprek"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* conversation list */}
          <div className="flex-1 overflow-y-auto py-2 px-2">
            {loadingConvs && (
              <div className="flex justify-center py-6">
                <svg className="w-5 h-5 text-white/30 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            )}
            {!loadingConvs && conversations.length === 0 && (
              <div className="text-center py-8 px-3">
                <div className="text-3xl mb-2">💬</div>
                <p className="text-white/30 text-xs leading-relaxed">Nog geen gesprekken. Start een chat en het wordt hier opgeslagen.</p>
              </div>
            )}
            {conversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => openConversation(conv)}
                className={`group w-full text-left px-3 py-3 rounded-xl mb-1 transition-all duration-150 ${
                  currentConvId === conv.id
                    ? 'bg-white/15 border border-white/20'
                    : 'hover:bg-white/8 border border-transparent'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-sm">💬</span>
                      <p className="text-white/90 text-xs font-medium truncate leading-snug">{conv.title}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-white/35 text-xs">{formatDate(conv.updated_at)}</span>
                      <span className="text-white/20 text-xs">· {conv.messages.length} berichten</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => deleteConversation(e, conv.id)}
                    className="opacity-0 group-hover:opacity-100 flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-500/20 transition-all"
                  >
                    {deletingId === conv.id
                      ? <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    }
                  </button>
                </div>
              </button>
            ))}
          </div>

          {/* sidebar footer note */}
          <div className="px-4 py-3 border-t border-white/10">
            <p className="text-white/25 text-xs text-center">Max. 5 gesprekken opgeslagen</p>
          </div>
        </div>
      </div>

      {/* ── main chat area ── */}
      <div className="relative z-10 flex flex-col flex-1 min-w-0">

        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10"
          style={{ background: 'var(--sidebar-bg)', backgroundSize: 'cover' }}>
          <div className="flex items-center gap-3">
            {/* sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(o => !o)}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
              title={sidebarOpen ? 'Verberg geschiedenis' : 'Toon geschiedenis'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
            </button>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xl"
              style={{ background: 'var(--chat-avatar)' }}>
              ✨
            </div>
            <div>
              <h1 className="text-white font-bold text-base leading-tight">
                {currentConvId
                  ? (conversations.find(c => c.id === currentConvId)?.title?.slice(0, 40) || 'AI Assistent')
                  : 'AI Assistent'}
              </h1>
              <p className="text-white/40 text-xs">Claude Sonnet · vraag alles</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/30">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-green-300 text-xs font-medium">Online</span>
            </div>
            {messages.length > 0 && (
              <button onClick={newChat}
                className="px-3 py-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 text-xs transition-all">
                + Nieuw
              </button>
            )}
          </div>
        </div>

        {/* messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4 bg-white"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(var(--brand-rgb),0.2) transparent' }}>

          {/* empty state */}
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full gap-8 animate-fade-in">
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-5xl mx-auto mb-4 shadow-sm"
                  style={{ background: 'var(--chat-avatar)' }}>
                  ✨
                </div>
                <h2 className="text-gray-900 text-2xl font-bold mb-2">Hoe kan ik je helpen?</h2>
                <p className="text-gray-400 text-sm max-w-md">
                  Stel vragen over reizen, bestemmingen, marketing, talen of wat je maar wilt.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl w-full">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)}
                    className="text-left px-4 py-3 rounded-xl text-sm text-gray-600 hover:text-gray-900 bg-white border border-gray-200 transition-all duration-200 hover:scale-105 hover:shadow-md"
                    style={{ borderColor: 'rgba(var(--brand-rgb), 0.15)' }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* bubbles */}
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-base mr-2 flex-shrink-0 mt-1"
                  style={{ background: 'var(--chat-avatar)' }}>
                  ✨
                </div>
              )}
              <div className="max-w-[75%]">
                <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${msg.role === 'user' ? 'text-white rounded-br-md' : 'text-gray-800 rounded-bl-md'}`}
                  style={msg.role === 'user' ? {
                    background: 'var(--chat-bubble)',
                    boxShadow: '0 4px 20px var(--chat-shadow)',
                  } : {
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}>
                  {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
                </div>
                <p className={`text-xs text-gray-400 mt-1 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                  {formatTime(msg.ts)}
                </p>
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white ml-2 flex-shrink-0 mt-1"
                  style={{ background: 'var(--chat-bubble)' }}>
                  {user?.agent_name?.[0]?.toUpperCase() || 'J'}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex justify-start animate-fade-in">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-base mr-2 flex-shrink-0"
                style={{ background: 'var(--chat-avatar)' }}>✨</div>
              <div className="rounded-2xl rounded-bl-md"
                style={{ background: 'white', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <TypingDots />
              </div>
            </div>
          )}

          {error && (
            <div className="flex justify-center">
              <div className="px-4 py-2 rounded-xl text-sm text-red-700 bg-red-50 border border-red-200">
                ⚠️ {error}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Context selectors — booking + client */}
        {bookings.length > 0 && (
          <div className="px-4 pt-3 pb-2 bg-white border-t border-gray-100 flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* Booking */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-400 flex-shrink-0">🗂️ Boeking:</span>
              <input
                type="text"
                value={bookingSearch}
                onChange={e => setBookingSearch(e.target.value)}
                placeholder="Zoek op naam, boek.nr., vertrekdatum…"
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-200 w-48"
              />
              <select
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-200 max-w-xs"
                value={selectedBookingId}
                onChange={e => setSelectedBookingId(e.target.value)}
              >
                <option value="">— Geen boeking —</option>
                {bookings.filter(b => {
                  if (!bookingSearch.trim()) return true;
                  const q = bookingSearch.toLowerCase();
                  const names = (Array.isArray(b.traveler_names) ? b.traveler_names.join(' ') : (b.traveler_names || '')).toLowerCase();
                  const dep = b.departure_date ? b.departure_date.toLowerCase() : '';
                  const depFormatted = b.departure_date ? new Date(b.departure_date).toLocaleDateString('nl-NL').toLowerCase() : '';
                  const dob = b.date_of_birth ? b.date_of_birth.toLowerCase() : '';
                  const dobFormatted = b.date_of_birth ? new Date(b.date_of_birth).toLocaleDateString('nl-NL').toLowerCase() : '';
                  const bookingNr = (b.booking_number || b.booking_nr || b.id || '').toString().toLowerCase();
                  return names.includes(q) || dep.includes(q) || depFormatted.includes(q) || dob.includes(q) || dobFormatted.includes(q) || bookingNr.includes(q);
                }).map(b => {
                  const names = Array.isArray(b.traveler_names) ? b.traveler_names.join(' & ') : (b.traveler_names || 'Onbekend');
                  const dep = b.departure_date ? new Date(b.departure_date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '?';
                  return (
                    <option key={b.id} value={b.id}>{names} — {b.destination || '?'} ({dep})</option>
                  );
                })}
              </select>
              {selectedBookingId && (
                <button onClick={() => { setSelectedBookingId(''); setBookingSearch(''); }} className="text-xs text-red-400 hover:text-red-600">× wissen</button>
              )}
            </div>

            {/* Client */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 flex-shrink-0">👤 Klant:</span>
              <div className="w-56">
                <ClientSearch
                  selectedClient={selectedClient}
                  onSelect={setSelectedClient}
                  onClear={() => setSelectedClient(null)}
                />
              </div>
            </div>
          </div>
        )}

        {/* input bar */}
        <div className="px-4 pb-5 pt-3 border-t border-gray-200 bg-white">
          <div className="flex items-end gap-3 p-3 rounded-2xl bg-white border border-gray-200 shadow-sm">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Stel een vraag… (Enter = versturen · Shift+Enter = nieuwe regel)"
              rows={1}
              disabled={loading}
              className="flex-1 bg-transparent text-gray-800 placeholder-gray-400 text-sm resize-none outline-none leading-relaxed"
              style={{ maxHeight: 160 }}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 active:scale-95"
              style={{ background: input.trim() && !loading ? 'var(--chat-avatar)' : '#e5e7eb' }}>
              {loading
                ? <svg className="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                : <svg className={`w-4 h-4 ${input.trim() && !loading ? 'text-white' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
              }
            </button>
          </div>
          <p className="text-center text-gray-400 text-xs mt-2">
            Claude Sonnet 4.5 · antwoorden zijn AI-gegenereerd
          </p>
        </div>
      </div>

      <style>{`
        @keyframes chatFloatA { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(60px,-80px) scale(1.1)} 66%{transform:translate(-40px,60px) scale(0.95)} }
        @keyframes chatFloatB { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-80px,40px) scale(1.05)} 66%{transform:translate(60px,-60px) scale(1.1)} }
        @keyframes chatFloatC { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-60px,-40px) scale(1.15)} }
        @keyframes chatBounce { 0%,100%{transform:translateY(0);opacity:.6} 50%{transform:translateY(-6px);opacity:1} }
      `}</style>
    </div>
  );
}
